import { siloIncentivesControllerAbi } from "@silo/contract-abi-types";
import {
  Address,
  ChainPublicClientService,
  getChainKeyFromProtocol,
  makeAmount,
  PublicClientService,
  USD_ZERO,
} from "@silo/types";
import { convertToUsd } from "@silo/utils";
import { Effect, Option, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "../contracts-client/types";
import { GLOBAL_CONFIG } from "@silo/config";
import { KvClient } from "../kv-client";
import { KvClientError, KvService } from "../kv-client/types";
import { NoSuchElementException } from "effect/Cause";
import { ParseError } from "@effect/schema/ParseResult";
import { RevenueData } from "./types";
import { isNullable } from "effect/Predicate";

const {
  rewardTokenAddress,
  protocolKey,
  rewardTokenDecimals,
  shareTokenAddress,
} = GLOBAL_CONFIG.revenue;

const chainKey = getChainKeyFromProtocol(protocolKey);

export function getRevenueData(
  user: Address | null
): Effect.Effect<
  RevenueData,
  ContractsClientError | KvClientError | ParseError | NoSuchElementException,
  KvService | PublicClientService
> {
  return Effect.gen(function* () {
    const { publicClient } = yield* ChainPublicClientService;

    const siloIncentivesControllerContract = getContract({
      address: GLOBAL_CONFIG.revenue.siloIncentivesControllerAddress,
      abi: siloIncentivesControllerAbi,
      client: publicClient,
    });

    const revenueBalance = yield* Effect.tryPromise({
      try: async () => {
        if (isNullable(user)) {
          return makeAmount(0n);
        }

        const balance =
          await siloIncentivesControllerContract.read.getRewardsBalance([
            [shareTokenAddress],
            user,
          ]);

        return makeAmount(balance);
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const tokenPriceMap = yield* KvClient.getTokenPriceMap([
      { tokenAddress: rewardTokenAddress, chainKey },
    ]);

    const rewardTokenPriceUsd = pipe(
      tokenPriceMap.get({
        tokenAddress: rewardTokenAddress,
        chainKey,
      }),
      Option.getOrElse(() => USD_ZERO)
    );

    const revenueBalanceUsd = convertToUsd(
      revenueBalance,
      rewardTokenDecimals,
      rewardTokenPriceUsd
    );

    const revenueData: RevenueData = {
      revenueBalance,
      revenueBalanceUsd,
    };

    return revenueData;
  }).pipe(
    // TODO: abstraction for providing chain public client by protocolKey
    Effect.provideServiceEffect(
      ChainPublicClientService,
      Effect.gen(function* () {
        const { getPublicClient } = yield* PublicClientService;
        const publicClient = getPublicClient(protocolKey);

        return { publicClient };
      })
    )
  );
}
